import {
  Tooltip,
  makeStyles,
  AlertDialog,
  ConfirmationDialog,
  Link,
  MenuItem,
  SplitMenuButton,
  InvoiceIconNext,
  MoneyIconRounded
} from '@paintscout/ui'
import type { ButtonProps as MuiButtonProps, MuiTheme as Theme, WithUseStyles } from '@paintscout/ui'
import {
  ExportJsonDialog,
  DialogStackContext,
  useClientOptions,
  useUser,
  useUserPermissions
} from '@paintscout/ui-business'
import ArchiveIcon from '@material-ui/icons/Archive'
import MoneyIcon from '@material-ui/icons/AttachMoney'
import AcceptIcon from '@material-ui/icons/Check'
import DeleteIcon from '@material-ui/icons/Delete'
import DescriptionIcon from '@material-ui/icons/Description'
import CopyIcon from '@material-ui/icons/FileCopy'
import FormIcon from '@material-ui/icons/FormatAlignLeft'
import ExportIcon from '@material-ui/icons/ImportExport'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import PDFIcon from '@material-ui/icons/PictureAsPdf'
import ShareIcon from '@material-ui/icons/Share'

const PRODUCT_ORDER_FORM = 'product-order-form'
const WORK_ORDER = 'work-order'
import SmsIcon from '@material-ui/icons/Sms'
import UnarchiveIcon from '@material-ui/icons/Unarchive'
import {
  acceptQuote,
  approveAdditionalWork,
  convertQuoteType,
  convertToEmptyInvoice,
  convertToInvoice,
  convertToTemplate,
  createDealFromQuote,
  createQuoteTemplateOption,
  getCrews,
  getDetails,
  getFeature,
  getFeatures,
  getObjectLabels,
  getQuoteOptions,
  getQuoteTypeOption,
  hasIntegrationInterface,
  isSignatureRequired,
  isUserOwner
} from '@paintscout/util/builder'
import { replaceQuoteItems } from '@paintscout/util/builder/quote/util/replace-quote-items'
import type { QuoteView } from '@paintscout/react-quote'
import {
  AcceptQuoteDialog,
  EditQuoteTemplateOptionDialog,
  QuoteContext,
  QuotePdfDialog,
  CollectPayrixPaymentDialog,
  CollectStripePaymentDialog,
  DepositPromptDialog,
  RequestReviewDialog
} from '@paintscout/react-quote'
import CopyQuoteDialog from 'src/components/dialogs/CopyQuoteDialog'
import ProcessEventDialog from 'src/components/dialogs/ProcessEventDialog'
import QuoteLinkDialog from 'src/components/dialogs/QuoteLinkDialog'

import { useApolloClient } from '@apollo/react-hooks'
import get from 'lodash/get'
import { useSnackbar } from 'notistack'
import type { OptionsDocument, QuoteDocument, UrlViewType, UserAssignments } from 'paintscout'
import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useDealMutations, useQuoteMutations, useQuotePermissions } from 'shared/hooks'
import type { CreateInvoiceArgs } from 'src/components/dialogs/InvoicesDialog'
import InvoicesDialog from 'src/components/dialogs/InvoicesDialog'
import QuotePreviewDialog from 'src/components/dialogs/QuotePreviewDialog'
import SelectStaffDialog from 'src/components/dialogs/SelectStaffDialog'
import SendQuoteDialog from 'src/components/dialogs/SendQuoteDialog'
import { cachedQuotesDelete } from 'src/util/cache'

import { isEmailValid } from '@paintscout/util/util'
import getDepositAmount from '@paintscout/util/util/getDepositAmount/getDepositAmount'
import * as Sentry from '@sentry/react'
import { useConnection } from 'react-detect-offline'
import QuickbooksLogo from 'src/public/images/providers/quickbooks.png'
import SageLogo from 'src/public/images/providers/sage.png'
import SalesforceLogo from 'src/public/images/providers/salesforce.png'
import ServiceTitanLogo from 'src/public/images/providers/serviceTitan.png'
import WorkglueLogo from 'src/public/images/providers/workglue.png'
import XeroLogo from 'src/public/images/providers/xero.png'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { goToDeal } from 'src/util/routing'
import { track } from 'shared/util/analytics'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2)
  },
  menuItemDivider: {
    padding: 0,
    height: 0,
    minHeight: 0,
    paddingBottom: theme.spacing(),
    marginBottom: theme.spacing(),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  buttons: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between'
    }
  },
  moreButton: {
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    }
  },
  saveButton: {}
}))

export interface SendButtonProps extends WithUseStyles<typeof useStyles> {
  className?: string
  /**
   * If true, send is replaced with save when dirty
   */
  disabled?: boolean
  requireSave?: boolean
  isNew?: boolean
  view: QuoteView
  handleChangeView?: (view: QuoteView) => void
  size?: MuiButtonProps['size']
}

function SendButton({ view, requireSave, isNew, handleChangeView, ...props }: SendButtonProps) {
  const classes = useStyles(props)
  const {
    saveQuote: saveQuoteMutation,
    deleteQuotes,
    archiveQuotes,
    requestReview,
    acceptQuote: acceptQuoteMutation,
    acceptAdditionalWork: acceptAdditionalWorkMutation
  } = useQuoteMutations()
  const apolloClient = useApolloClient()
  const { online } = useConnection()

  const { crm: crmFlag } = useFlags()

  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)

  const { enqueueSnackbar } = useSnackbar()
  const {
    quote,
    isSaving,
    isDirty,
    save: saveQuote,
    sync: syncQuote,
    updateQuote,
    hasActiveAccount
  } = useContext(QuoteContext)

  const { saveDeal } = useDealMutations()

  const { options, clientId, updateOptions, save: saveOptions, rates } = useClientOptions()
  const { isSuperadmin, user, preferences, isSupport, isMasquerading } = useUser()
  const { allowPaymentsOnQuotes, depositAmount, allowedDepositTypes } = getQuoteOptions({
    quote,
    options
  })
  const { permissionToEditAccountSettings, permissionToCreateDeal } = useUserPermissions()
  const history = useHistory()
  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })
  const features = getFeatures({ options })
  const hasSmartTemplate = getFeature({ options, path: 'quotes.smartTemplate' })
  const hasInvoice = !!quote.child_invoice || quote.child_invoices?.length === 1
  const childInvoiceId = quote.child_invoice || quote.child_invoices?.[0]
  const isOwner = isUserOwner(quote, user.user_id)

  const {
    permissionToEditQuote,
    permissionToAcceptQuote,
    permissionToEditInvoice,
    permissionToSendQuote,
    permissionToSendInvoice,
    permissionToCreateQuote,
    permissionToCreateInvoice,
    permissionToAssignDocuments,
    permissionToAcceptPayments
  } = useQuotePermissions(quote)

  const canMakeInvoice =
    !hasInvoice &&
    features.invoices &&
    features.invoices.enabled &&
    !quote?.is_invoice &&
    (permissionToCreateInvoice || isOwner)

  const canMakeDeal = crmFlag && !quote.is_invoice && !quote.deal_id && permissionToCreateDeal

  // TODO: we are assuming if you can view the quote, you can view the deal, e.g., EditQuoteInfoDialog. We can tighten this up when we do User Perms
  const canViewDeal = crmFlag && quote.deal_id

  const canRequestGoogleReview = features.requestGoogleReview?.enabled
  const hasSage = hasIntegrationInterface({ preferences, options, integration: 'sage' })
  const hasQuickbooks = hasIntegrationInterface({ preferences, options, integration: 'intuit' })
  const hasServiceTitan = hasIntegrationInterface({ preferences, options, integration: 'serviceTitan' })
  const hasSalesForce = hasIntegrationInterface({ preferences, options, integration: 'salesforce' })
  const hasSalesForceSandbox = hasIntegrationInterface({ preferences, options, integration: 'salesforcesandbox' })
  const hasFieldflow = hasIntegrationInterface({ preferences, options, integration: 'fieldflow' })
  const hasWorkglue =
    hasIntegrationInterface({ preferences, options, integration: 'workglue' }) &&
    !quote?.is_invoice &&
    quote.source?.provider === 'workglue'
  const hasWorkglueV2 =
    hasIntegrationInterface({ preferences, options, integration: 'workgluev2' }) &&
    !quote?.is_invoice &&
    quote.source?.provider === 'workglue'

  const hasXero = hasIntegrationInterface({ preferences, options, integration: 'xero' })
  const hasIntuitInvoice = !!quote?.sources?.intuit?.invoiceId
  const hasIntuitDepositInvoice = !!quote?.sources?.intuit?.depositInvoiceId
  const intuitInvoiceUrl = quote?.sources?.intuit?.invoiceUrl
  const intuitDepositInvoiceUrl = quote?.sources?.intuit?.depositInvoiceUrl

  const templatesFeature = get(features, 'quotes.templates.enabled')
  const multipleInvoicesFeature = get(features, 'quotes.multipleInvoices')
  const validContactEmail = isEmailValid(quote?.contact?.email)
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = hasStripe || hasStripeW1d || hasPayrix

  const shouldRequestDeposit =
    allowPaymentsOnQuotes &&
    hasPayments &&
    !!depositAmount?.value &&
    !quote.is_invoice &&
    quote.totals.amount_paid === 0 &&
    quote.totals.balance_due > 0 &&
    !quote.depositInfo &&
    online &&
    permissionToAcceptPayments

  const details = getDetails({ quote, options })
  const productNote = details.notes.products.useCustom
    ? (details.notes.products.custom as string)
    : (details.notes.products.default as string)
  const showProductOrder = get(features, 'quotes.productOrderForm', false) && productNote !== ''

  const isTemplate = quote.type === 'quoteTemplate'

  function handleProductOrderForm() {
    if (handleChangeView) {
      handleChangeView(PRODUCT_ORDER_FORM)
    }
  }

  function handleRequestReview() {
    track('quote_review_requested', { quoteId: quote._id })
    const { message, link } = options.options.reviewRequestMessage
    const content = `${message.trimEnd()} ${link}`
    openDialog(RequestReviewDialog, {
      options,
      contact: quote.contact,
      onConfirm: (phoneNumber: string) => {
        dismissDialog()
        void requestReview({
          sms: {
            to: phoneNumber
          },
          content
        })
          .catch(() => {
            enqueueSnackbar('An error occurred while sending a review request.', { variant: 'error' })
          })
          .then((result) => {
            if (result) {
              enqueueSnackbar('Message sent!', { variant: 'success' })
            }
            return result
          })
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleSaveAsTemplate() {
    track('quote_template_saved', { quoteId: quote._id, quoteType: quote.int_ext?.value })
    const quoteTemplate = createQuoteTemplateOption({
      quoteTypes: [quote.int_ext.value],
      smartTemplate: hasSmartTemplate
    })

    openDialog(EditQuoteTemplateOptionDialog, {
      item: quoteTemplate,
      options,
      onConfirm: async (updatedOptions: OptionsDocument, type: string) => {
        dismissDialog()
        updateOptions({ options: updatedOptions })
        const updatedQuote = isDirty ? await saveQuote() : quote
        const templatedQuote = convertToTemplate({
          quote: updatedQuote,
          key: quoteTemplate.key,
          smartTemplate: hasSmartTemplate
        })
        const convertedQuote = hasSmartTemplate
          ? (replaceQuoteItems({
              quote: templatedQuote,
              options,
              rates
            }) as QuoteDocument)
          : templatedQuote

        const typeOption = getQuoteTypeOption({
          options,
          quoteType: type
        })

        const typeConvertedQuote = convertQuoteType({
          quote: convertedQuote,
          quoteType: typeOption,
          quoteTemplate,
          options
        })

        await saveQuoteMutation({
          quote: typeConvertedQuote
        })
          .then(() => {
            saveOptions()
          })
          .catch(() => {
            enqueueSnackbar('An error occurred while saving template', { variant: 'error' })
            // redirect user back to the parent quote
            history.replace(`/quotes/edit/${quote._id}/`)
          })

        history.push(`/quotes/edit/${quoteTemplate.key}`)
      },
      onCancel: dismissDialog
    })
  }

  async function handleArchiveQuote({ unarchive }: { unarchive?: boolean } = {}) {
    track(unarchive ? 'quote_unarchived' : 'quote_archived', { quoteId: quote._id })
    try {
      if (isDirty) {
        await saveQuote()
      }
      await archiveQuotes([quote._id], { unarchive })
    } catch (e) {
      console.error(e)
      enqueueSnackbar(`An error occurred while ${unarchive ? 'unarchiving' : 'archiving'}`, { variant: 'error' })
    }
  }

  function handleInvoices() {
    openDialog(InvoicesDialog, {
      onCancel: dismissAllDialogs,
      onCreateInvoice: ({ partialAmount, empty }: CreateInvoiceArgs) =>
        handleCreateInvoice({ multiple: true, partialAmount, empty })
    })
  }

  async function handleCreateInvoice(args: {
    multiple?: boolean
    partialAmount?: number
    empty?: boolean
    redirect?: boolean
  }) {
    track('invoice_created', {
      quoteId: quote._id,
      invoiceType: args.empty ? 'empty' : args.partialAmount ? 'partial' : 'full',
      amount: args.partialAmount
    })
    let currentQuote = quote
    if (isDirty) {
      await saveQuote()
    } else {
      const syncedQuote = await syncQuote()
      if (syncedQuote) {
        currentQuote = syncedQuote
      }
    }
    const { partialAmount, empty, redirect = true } = args
    const invoice = empty
      ? convertToEmptyInvoice({ quote: currentQuote, options })
      : convertToInvoice({ quote: currentQuote, options, partialAmount })

    // Awaiting save so we dont show up to invoice page with no rev and get double saved.
    void saveQuoteMutation({ quote: invoice })
      .then((_) => {
        if (redirect) {
          history.push(`/invoices/edit/${invoice._id}/`)
        }
      })
      .catch((e) => {
        console.error(e)
        enqueueSnackbar('An error occurred while creating the invoice', { variant: 'error' })
        // redirect user back to the parent quote
        history.replace(`/quotes/edit/${currentQuote._id}/`)

        // remove the invoice _id so it can be retried
        saveQuoteMutation({
          quote: {
            ...currentQuote,
            child_invoice: null
          }
        })
      })
  }

  async function handleCreateDeal() {
    track('deal_created_from_quote', { quoteId: quote._id })
    const contact = quote.contact
    if (!contact) {
      openDialog(AlertDialog, {
        message: 'You must add a contact to create a deal',
        onConfirm: dismissDialog
      })
      return
    }
    const deal = createDealFromQuote({ quote, options })
    try {
      const dealRes = await saveDeal({ deal })
      await saveQuoteMutation({
        quote: {
          ...quote,
          deal_id: deal._id
        }
      })
      goToDeal(dealRes.deal?._id, true, { newlyLinkedQuoteId: quote._id })
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
      enqueueSnackbar('Error saving deal', { variant: 'error' })
    }
  }

  async function handleCopyQuote() {
    track('quote_duplicated', { sourceQuoteId: quote._id, isInvoice: quote.is_invoice })
    if (isDirty) {
      await saveQuote()
    }
    openDialog(CopyQuoteDialog, {
      quoteId: quote._id,
      isInvoice: quote.is_invoice
    })
  }

  function handleDeleteQuote() {
    track('quote_deleted', { quoteId: quote._id, isInvoice: quote.is_invoice })
    const { _id, is_invoice } = quote

    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: `This ${objectLabels.quote.value.toLowerCase()} will no longer be accessible and you cannot undo this action.`,
      yesLabel: "Yes, I'm sure",
      noLabel: 'Back',
      closeButton: true,
      scary: false,
      onConfirm: async (_: React.MouseEvent<HTMLElement>) => {
        await cachedQuotesDelete([quote._id], apolloClient)
        // don't await because cache will remove the quote and quote page will be showing a loading shim
        deleteQuotes([quote._id], true).catch((e) => {
          Sentry.captureException(e, { tags: { quoteId: _id } })
          enqueueSnackbar(`Unable to delete ${objectLabels.quote.value}`, { variant: 'error' })
        })
        setTimeout(() => {
          history.push(`/${is_invoice ? 'invoices' : 'quotes'}/`, { skipDirtyPrompt: true, fullLoad: true })
        }, 1000)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleGetLink(viewOverride: 'quote' | typeof WORK_ORDER) {
    track('quote_link_generated', { quoteId: quote._id, viewType: viewOverride })
    openDialog(QuoteLinkDialog, {
      view: viewOverride,
      onConfirm: dismissDialog
    })
  }

  function handleExportJson() {
    track('quote_json_exported', { quoteId: quote._id })
    openDialog(ExportJsonDialog, {
      item: quote,
      onConfirm: dismissDialog
    })
  }

  function handleUnlinkInvoice() {
    openDialog(ConfirmationDialog, {
      title: 'Unlink invoice from this quote?',
      message: (
        <>
          This will clear the linked invoice (<strong>{childInvoiceId}</strong>) from this{' '}
          {objectLabels.quote.value.toLowerCase()} and save immediately.
          <br />
          <br />
          <strong>This action cannot be undone.</strong> The invoice document itself will not be deleted, but the link
          between this {objectLabels.quote.value.toLowerCase()} and the invoice will be removed.
        </>
      ),
      yesLabel: 'Yes, unlink invoice',
      noLabel: 'Cancel',
      closeButton: true,
      scary: true,
      onConfirm: async () => {
        try {
          await saveQuoteMutation({
            quote: {
              ...quote,
              child_invoice: null,
              child_invoices: null,
              child_invoice_number: null,
              child_invoice_numbers: null
            }
          })
          enqueueSnackbar('Invoice unlinked', { variant: 'success' })
        } catch (e) {
          Sentry.captureException(e, { tags: { quoteId: quote._id } })
          enqueueSnackbar('Failed to unlink invoice', { variant: 'error' })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handlePreview() {
    track('quote_previewed', { quoteId: quote._id, viewType: view })
    openDialog(QuotePreviewDialog, {
      view: view as 'quote' | typeof WORK_ORDER | 'archived-items' | 'activity',
      onConfirm: (pending?: boolean, previewQuote?: QuoteDocument) => handleAcceptClick(previewQuote ?? quote, pending),
      onClose: dismissDialog
    })
  }

  async function acceptAdditionalWork(quote: QuoteDocument, signature?: string, note?: string) {
    track('quote_additional_work_accepted', { quoteId: quote._id })
    let updatedQuote: QuoteDocument = quote
    try {
      if (online && quote?._rev) {
        // Only use full accept if online and quote has a _rev
        const res = await acceptAdditionalWorkMutation({
          rev: quote._rev,
          signature,
          quoteId: quote._id
        })
        const { quote: mutatedQuote } = res.data.acceptAdditionalWork
        updateQuote({ quote: mutatedQuote, autosave: false, blockDirty: true })
      } else {
        // Offline accept, no notification but works offline/ w/o save
        updatedQuote = approveAdditionalWork({
          quote,
          options,
          note,
          view: quote.defaultView === 'substrate' ? 'substrate' : 'area',
          signature
        })
        await saveQuote(updatedQuote)
      }
      enqueueSnackbar('Accepted Additional Work', { variant: 'success' })
    } catch (error) {
      Sentry.captureException(error, { tags: { quoteId: quote._id } })
      enqueueSnackbar('Unable to accept additional work', { variant: 'error' })
      dismissDialog()
    }
  }

  async function accept(signature?: string, argsQuote?: QuoteDocument) {
    track('quote_accepted', { quoteId: quote._id, hasSignature: !!signature })
    let updatedQuote: QuoteDocument = quote
    try {
      if (online && quote?._rev) {
        // Only use full accept if online and quote has a _rev
        const res = await acceptQuoteMutation({
          quote: argsQuote || quote,
          signature,
          quoteId: quote._id
        })
        const { quote: mutatedQuote } = res.data.acceptQuote
        updatedQuote = mutatedQuote
      } else {
        // Offline accept, no notification but works offline/ w/o save
        updatedQuote = acceptQuote({ quote, view: 'quote' as UrlViewType, signature, acceptedVia: 'onTheSpot' })
      }
      await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
      enqueueSnackbar('Accepted', { variant: 'success' })
    } catch (error) {
      Sentry.captureException(error, { tags: { quoteId: quote?._id } })
      enqueueSnackbar('Unable to accept', { variant: 'error' })
      dismissDialog()
    }
  }

  async function handleAccept(quote: QuoteDocument, pending?: boolean, signature?: string) {
    const quoteAccepted = quote.status.value === 'accepted'
    if (pending && (quoteAccepted || quote.is_invoice)) {
      await acceptAdditionalWork(quote, signature)
    } else {
      await accept(signature, quote)
    }
    dismissDialog()
  }

  async function handlePromptDeposit() {
    track('deposit_prompted', { quoteId: quote._id })
    openDialog(DepositPromptDialog, {
      quote,
      options,
      onCancel: dismissAllDialogs,
      onPayDeposit: handleCollectDeposit
    })
  }

  async function handleCollectDeposit() {
    track('payment_collected', { quoteId: quote._id, processor: hasPayrix ? 'payrix' : 'stripe' })
    const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
    openDialog(
      PaymentDialog,
      {
        quote,
        isDeposit: true,
        amount: getDepositAmount({ quote, options }),
        source: 'preview',
        options,
        companyId: clientId,
        paymentType: allowedDepositTypes,
        onCancel: dismissAllDialogs,
        onConfirm: async (updatedQuote: QuoteDocument) => {
          dismissAllDialogs()
          await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
        }
      },
      { replace: true }
    )
  }

  async function handleAcceptClick(quote: QuoteDocument, pending?: boolean) {
    const requireSignature = isSignatureRequired({ options, quote, path: 'requireSignatureOnSpot' })

    if (isDirty) {
      enqueueSnackbar(
        `The ${objectLabels.quote.value} must be saved before approving ${pending ? 'additional ' : ''}work.`,
        {
          variant: 'error'
        }
      )
    } else if (shouldRequestDeposit && !pending && quote?.contact && !validContactEmail) {
      enqueueSnackbar(`The contact must have a valid email address before approving and accepting deposit.`)
    } else {
      if (requireSignature) {
        openDialog(AcceptQuoteDialog, {
          quote,
          options,
          onConfirm: async (ev: any, signature: string) => {
            await handleAccept(quote, pending, signature).then(() => {
              if (shouldRequestDeposit && !pending) {
                handlePromptDeposit()
              }
            })
          },
          onCancel: dismissDialog
        })
      } else {
        await handleAccept(quote, pending).then(() => {
          if (shouldRequestDeposit && !pending) {
            handlePromptDeposit()
          }
        })
      }
    }
  }

  function handleReview() {
    track('quote_send_initiated', { quoteId: quote._id, viewType: view, isInvoice: quote.is_invoice })
    let title: string
    let message: string
    if ((!quote?.is_invoice && !permissionToSendQuote) || (quote?.is_invoice && !permissionToSendInvoice)) {
      title = 'Cannot Send'
      message = 'You do not have sufficient permissions to send'
    } else if (view !== 'quote' && view !== WORK_ORDER && view !== 'presentation' && view !== PRODUCT_ORDER_FORM) {
      title = 'Cannot Send'
      message = `Only ${objectLabels.quote.plural.toLowerCase()} and ${objectLabels.workOrder.plural.toLowerCase()} can be sent`
    } else if (!quote.contact) {
      title = 'Contact Required'
      message = `You must add a contact before you can send`
    } else if (!quote._rev) {
      title = 'Save Required'
      message = `A save is required before sending the ${objectLabels.quote.value}.`
    }

    if (title) {
      openDialog(AlertDialog, {
        title,
        message,
        onConfirm: dismissDialog
      })
    } else {
      openDialog(SendQuoteDialog, {
        view: view as 'quote' | typeof WORK_ORDER | typeof PRODUCT_ORDER_FORM | 'presentation',
        onConfirm: dismissDialog,
        onCancel: dismissDialog
      })
    }
  }

  async function handleDownloadPDF(viewOverride: 'quote' | typeof WORK_ORDER | typeof PRODUCT_ORDER_FORM) {
    track('quote_pdf_downloaded', { quoteId: quote._id, viewType: viewOverride })
    if (isDirty) {
      await saveQuote()
    }
    openDialog(QuotePdfDialog, {
      quote,
      companyId: clientId,
      options,
      silent: true,
      view: viewOverride,
      onConfirm: dismissDialog
    })
  }

  function handleShare() {
    track('quote_shared_with_staff', { quoteId: quote._id })
    openDialog(SelectStaffDialog, {
      currentUsers: quote?.assignedUsers,
      currentCrewIds: quote?.assignedCrewIds ?? [],
      ownerId: quote?.owner?.id,
      onConfirm: async (newAssignedUsers: UserAssignments, crewIds: string[]) => {
        // Expand crew members into assignedUsers so painters in those crews can see the document
        const crews = getCrews({ options })
        const expandedUsers = { ...newAssignedUsers }
        for (const crewId of crewIds) {
          const crew = crews.find((c) => c._id === crewId)
          if (!crew) continue
          const members = [crew.projectManager, crew.siteLead, ...(crew.members ?? [])].filter(Boolean)
          for (const member of members) {
            if (!member.id) continue
            const memberId = member.id.replace(/^auth0\|/, '')
            if (!expandedUsers[memberId]) {
              expandedUsers[memberId] = {
                assigned: true,
                name: [member.firstName, member.lastName].filter(Boolean).join(' ')
              }
            }
          }
        }

        await saveQuote({
          ...quote,
          assignedUsers: expandedUsers,
          assignedCrewIds: crewIds
        })

        dismissDialog()
        enqueueSnackbar(`${objectLabels.quote.value} shared with selected staff`, { variant: 'success' })
      },
      onCancel: dismissDialog
    })
  }

  function handleIntegrationClick({
    provider,
    title,
    async,
    params,
    requireContact,
    syncAfter
  }: {
    provider: string
    title: string
    async?: boolean
    params?: any
    requireContact?: boolean
    syncAfter?: boolean
  }) {
    return async function () {
      track('quote_integration_triggered', { quoteId: quote._id, provider, action: title })
      if (requireContact && !quote.contact) {
        openDialog(AlertDialog, {
          title: 'Contact Required',
          message: `You must add a contact before you can send to ${provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()}`,
          onConfirm: dismissDialog
        })
      } else {
        if (isDirty) {
          await saveQuote()
        }

        openDialog(ProcessEventDialog, {
          provider,
          // eventTypeOverride
          title,
          async,
          params: {
            quoteId: quote._id,
            ...params
          },
          onClose: () => {
            dismissDialog()
          },
          ...(syncAfter && {
            onConfirm: () => {
              syncQuote()
            }
          })
        })
      }
    }
  }

  const showSave = requireSave && isDirty
  const showPreview = view === 'quote' || view === 'presentation'

  const canCopy = (quote?.is_invoice && permissionToCreateInvoice) || (!quote?.is_invoice && permissionToCreateQuote)

  return (
    <Tooltip title={hasActiveAccount ? '' : `Your trial has expired`} placement="top">
      <SplitMenuButton
        data-testid="send-button"
        label={showSave ? 'Save' : 'Send...'}
        loading={showSave && isSaving}
        leftDisabled={isSaving}
        rightDisabled={isSaving}
        onClick={() => (showSave ? saveQuote() : handleReview())}
        {...props}
      >
        {/* Preview */}
        {showPreview &&
          (permissionToAcceptQuote || quote.is_invoice) && [
            <MenuItem key="preview" icon={AcceptIcon} onClick={handlePreview}>
              {quote.is_invoice ? 'Preview' : 'Preview/Accept'}
            </MenuItem>,
            <MenuItem key="preview-divider" disabled classes={{ root: classes.menuItemDivider }} />
          ]}
        {/* primary items */}
        {permissionToAssignDocuments && (
          <MenuItem key="share-staff" icon={ShareIcon} onClick={handleShare}>
            Share with Staff
          </MenuItem>
        )}
        {multipleInvoicesFeature && ((quote.is_invoice && !!quote.parent_quote) || !quote.is_invoice) && !isNew && (
          <MenuItem key="invoices" icon={ShareIcon} onClick={handleInvoices}>
            Invoices
          </MenuItem>
        )}
        {canMakeDeal && (
          <MenuItem key="create-deal" icon={MoneyIconRounded} onClick={() => handleCreateDeal()}>
            Create Deal
          </MenuItem>
        )}
        {canViewDeal && (
          <Link key="view-deal" to={`/deals/edit/${quote.deal_id}`}>
            <MenuItem icon={MoneyIconRounded}>View Deal</MenuItem>
          </Link>
        )}
        {!multipleInvoicesFeature && canMakeInvoice && (
          <MenuItem key="create-invoice" icon={InvoiceIconNext} onClick={() => handleCreateInvoice({})}>
            Create Invoice
          </MenuItem>
        )}

        {!multipleInvoicesFeature && hasInvoice && (
          <Link key="view-invoice" to={`/quotes/edit/${childInvoiceId}`}>
            <MenuItem icon={InvoiceIconNext}>View Invoice</MenuItem>
          </Link>
        )}

        {quote?.is_invoice && !!quote.parent_quote && (
          <Link key="view-quote" to={`/quotes/edit/${quote.parent_quote}`}>
            <MenuItem icon={MoneyIcon}>View {getObjectLabels({ options, quote, invoice: false }).quote.value}</MenuItem>
          </Link>
        )}
        {permissionToEditAccountSettings && templatesFeature && !isNew && (
          <MenuItem key="create-template" icon={DescriptionIcon} onClick={handleSaveAsTemplate}>
            Save as Template
          </MenuItem>
        )}
        {canRequestGoogleReview && (
          <MenuItem key="request-google-review" icon={SmsIcon} onClick={handleRequestReview}>
            Request Google Review
          </MenuItem>
        )}
        {(permissionToAssignDocuments ||
          canMakeInvoice ||
          hasInvoice ||
          (quote?.is_invoice && quote.parent_quote) ||
          (permissionToEditAccountSettings && templatesFeature && !isNew)) && (
          <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
        )}
        {/* link items */}
        {permissionToSendQuote && (
          <MenuItem
            className="send-button-copy-quote-link"
            key="get-quote-link"
            icon={LinkIcon}
            onClick={() => handleGetLink('quote')}
          >
            Copy {objectLabels.quote.value} Link
          </MenuItem>
        )}
        <MenuItem
          className="send-button-copy-work-order-link"
          key="get-work-order-link"
          icon={LinkIcon}
          onClick={() => handleGetLink(WORK_ORDER)}
        >
          Copy {objectLabels.workOrder.value} Link
        </MenuItem>
        {!isNew && (
          <MenuItem
            className="send-button-download-quote"
            key="download-quote-pdf"
            icon={PDFIcon}
            onClick={async () => await handleDownloadPDF('quote')}
          >
            Download {objectLabels.quote.value}
          </MenuItem>
        )}
        {!isNew && (
          <MenuItem
            key="download-work-order-pdf"
            className="send-button-download-work-order"
            icon={PDFIcon}
            onClick={async () => await handleDownloadPDF(WORK_ORDER)}
          >
            Download {objectLabels.workOrder.value}
          </MenuItem>
        )}
        {!isNew && showProductOrder && (
          <MenuItem
            key="download-product-order-pdf"
            icon={PDFIcon}
            onClick={async () => await handleDownloadPDF(PRODUCT_ORDER_FORM)}
          >
            Download Product Order
          </MenuItem>
        )}
        {showProductOrder && view === WORK_ORDER && (
          <MenuItem key={PRODUCT_ORDER_FORM} icon={FormIcon} onClick={handleProductOrderForm}>
            Product Order Form
          </MenuItem>
        )}
        {(quote?.is_invoice && permissionToCreateInvoice) ||
          (!quote?.is_invoice && permissionToCreateQuote) ||
          (!isNew &&
            ((quote?.is_invoice && permissionToEditInvoice) || (!quote?.is_invoice && permissionToEditQuote)) && (
              <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
            ))}
        {/* CRUD */}
        {canCopy && (
          <MenuItem key="copy" icon={CopyIcon} onClick={handleCopyQuote}>
            {`Duplicate ${isTemplate ? 'Template' : objectLabels.quote.value}`}
          </MenuItem>
        )}
        {!isNew &&
          !quote.archived &&
          !isTemplate &&
          ((quote?.is_invoice && permissionToEditInvoice) || (!quote?.is_invoice && permissionToEditQuote)) && (
            <MenuItem key="archive" icon={ArchiveIcon} onClick={() => handleArchiveQuote()}>
              {`Archive ${isTemplate ? 'Template' : objectLabels.quote.value}`}
            </MenuItem>
          )}
        {!isNew &&
          quote.archived &&
          ((quote?.is_invoice && permissionToEditInvoice) || (!quote?.is_invoice && permissionToEditQuote)) && (
            <MenuItem key="unarchive" icon={UnarchiveIcon} onClick={() => handleArchiveQuote({ unarchive: true })}>
              {`Unarchive ${isTemplate ? 'Template' : objectLabels.quote.value}`}
            </MenuItem>
          )}
        {!isNew &&
          ((quote?.is_invoice && permissionToEditInvoice) || (!quote?.is_invoice && permissionToEditQuote)) && (
            <MenuItem key="delete" icon={DeleteIcon} onClick={handleDeleteQuote}>
              {`Delete ${isTemplate ? 'Template' : objectLabels.quote.value}`}
            </MenuItem>
          )}
        {/* Integrations */}

        {(hasQuickbooks || hasSage || hasWorkglue || hasXero || hasServiceTitan || hasFieldflow) && (
          <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
        )}
        {hasServiceTitan && (
          <MenuItem
            image={ServiceTitanLogo}
            onClick={handleIntegrationClick({
              provider: 'serviceTitan',
              async: false,
              params: {
                createInvoice: quote.is_invoice,
                syncOnComplete: true
              },
              title: `Create ${quote.is_invoice ? 'Invoice' : 'Estimate'} in ServiceTitan`
            })}
          >
            Create {quote.is_invoice ? 'Invoice' : 'Estimate'} in ServiceTitan
          </MenuItem>
        )}
        {hasSalesForce && (
          <>
            <MenuItem
              image={SalesforceLogo}
              onClick={handleIntegrationClick({
                provider: 'salesforce',
                async: false,
                params: {
                  createInvoice: quote.is_invoice,
                  async: true
                },
                title: `Sync with SalesForce`
              })}
            >
              Sync with SalesForce
            </MenuItem>
            {!!quote.is_invoice && (
              <MenuItem
                image={SalesforceLogo}
                onClick={handleIntegrationClick({
                  provider: 'salesforce',
                  async: false,
                  params: {
                    createInvoice: quote.is_invoice,
                    completeJob: true,
                    async: true
                  },
                  title: `Complete Job in SalesForce`
                })}
              >
                Complete Job in Salesforce
              </MenuItem>
            )}
          </>
        )}
        {hasSalesForceSandbox && (
          <>
            <MenuItem
              image={SalesforceLogo}
              onClick={handleIntegrationClick({
                provider: 'salesforcesandbox',
                async: false,
                params: {
                  createInvoice: quote.is_invoice,
                  async: true
                },
                title: `Sync with SalesForce (Sandbox)`
              })}
            >
              Sync with SalesForce (Sandbox)
            </MenuItem>
            {!!quote.is_invoice && (
              <MenuItem
                image={SalesforceLogo}
                onClick={handleIntegrationClick({
                  provider: 'salesforcesandbox',
                  async: false,
                  params: {
                    createInvoice: quote.is_invoice,
                    completeJob: true,
                    async: true
                  },
                  title: `Complete Job in SalesForce (Sandbox)`
                })}
              >
                Complete Job in SalesForce (Sandbox)
              </MenuItem>
            )}
          </>
        )}
        {hasQuickbooks && (
          <>
            <MenuItem
              image={QuickbooksLogo}
              onClick={handleIntegrationClick({
                provider: 'intuit',
                params: {
                  createInvoice: true
                },
                title: 'Create Invoice in QuickBooks',
                syncAfter: true
              })}
            >
              Create Invoice in QuickBooks
            </MenuItem>
            <MenuItem
              image={QuickbooksLogo}
              onClick={handleIntegrationClick({
                provider: 'intuit',
                params: {
                  createDeposit: true
                },
                title: 'Create Deposit in QuickBooks',
                syncAfter: true
              })}
            >
              Create Deposit in QuickBooks
            </MenuItem>
            {hasIntuitInvoice && (
              <MenuItem
                image={QuickbooksLogo}
                onClick={handleIntegrationClick({
                  provider: 'intuit',
                  params: {
                    updateInvoice: true
                  },
                  title: 'Update Invoice in QuickBooks',
                  syncAfter: true
                })}
              >
                Update Invoice in QuickBooks
              </MenuItem>
            )}
            {hasIntuitInvoice && intuitInvoiceUrl && (
              <MenuItem image={QuickbooksLogo} onClick={() => window.open(intuitInvoiceUrl, '_blank')}>
                View Invoice in QuickBooks
              </MenuItem>
            )}
            {hasIntuitDepositInvoice && intuitDepositInvoiceUrl && (
              <MenuItem image={QuickbooksLogo} onClick={() => window.open(intuitDepositInvoiceUrl, '_blank')}>
                View Deposit in QuickBooks
              </MenuItem>
            )}
            {quote?.additionalWork?.approvedDate && (
              <MenuItem
                image={QuickbooksLogo}
                onClick={handleIntegrationClick({
                  provider: 'intuit',
                  params: {
                    additionalWork: true
                  },
                  title: 'Create Additional Work Invoice in QuickBooks',
                  syncAfter: true
                })}
              >
                Create Additional Work Invoice in QuickBooks
              </MenuItem>
            )}
          </>
        )}
        {hasSage && (
          <MenuItem
            image={SageLogo}
            onClick={handleIntegrationClick({
              provider: 'sage',
              params: {
                isInvoice: quote.is_invoice
              },
              title: `Create ${objectLabels.quote.value.toLowerCase()} in Sage`
            })}
          >
            {`Create ${objectLabels.quote.value} in Sage`}
          </MenuItem>
        )}
        {hasWorkglue && (
          <>
            <MenuItem
              image={WorkglueLogo}
              onClick={handleIntegrationClick({
                provider: 'workglue',
                title: 'Send to Workglue',
                params: {
                  workglueVersion: 1
                }
              })}
            >
              Send to Workglue
            </MenuItem>
          </>
        )}
        {hasWorkglueV2 && (
          <>
            <MenuItem
              image={WorkglueLogo}
              onClick={handleIntegrationClick({
                provider: 'workgluev2',
                title: 'Send to Workglue V2'
              })}
            >
              Send to Workglue V2
            </MenuItem>
          </>
        )}
        {hasFieldflow && (
          <MenuItem
            onClick={handleIntegrationClick({
              provider: 'fieldflow',
              title: 'Send to Fieldflow'
            })}
          >
            Send to Fieldflow
          </MenuItem>
        )}
        {hasXero && (
          <MenuItem
            image={XeroLogo}
            onClick={handleIntegrationClick({
              provider: 'xero',
              params: {
                isInvoice: quote.is_invoice
              },
              title: `Create ${objectLabels.quote.value.toLowerCase()} in Xero`,
              requireContact: true
            })}
          >
            {`Create ${objectLabels.quote.value} in Xero`}
          </MenuItem>
        )}
        {(isSuperadmin || isSupport) && <MenuItem disabled classes={{ root: classes.menuItemDivider }} />}
        {(isSuperadmin || isSupport) && (
          <MenuItem key="export-json" icon={ExportIcon} onClick={handleExportJson}>
            {`Export ${view === WORK_ORDER ? objectLabels.workOrder.value : objectLabels.quote.value}`}
          </MenuItem>
        )}
        {isMasquerading && (isSuperadmin || isSupport) && hasInvoice && (
          <MenuItem key="unlink-invoice" icon={LinkOffIcon} onClick={handleUnlinkInvoice}>
            Unlink Invoice
          </MenuItem>
        )}
      </SplitMenuButton>
    </Tooltip>
  )
}

export default SendButton
